import React, {useState} from 'react';
import PaymentCard from "./PaymentCard";
import PaymentDirectDebit from "./PaymentDirectDebit";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe, Stripe} from "@stripe/stripe-js";
import {Invoice, Payment, PaymentStatusId} from "./types";
import {limeApiGet} from "../../utils/api";
import {getClaimValue} from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import {useAuth} from "react-oidc-context";
import LoadingSpinner from "../Shared/LoadingSpinner/LoadingSpinner";
import {LimeConfig} from "../../utils/limeConfig";
import {useLocation} from "react-router-dom";
import AlertCircle from "../../images/alert-circle.svg";
import {getFeatureFlag} from "../../utils/featureFlag";
import { portalCardSurface, portalPageTitle, portalPageSubtitle, portalSecondaryPillButton } from "../../theme/portalLayout";

function PaymentMethod() {
    const auth = useAuth();
    const [updatePaymentMethod] = useState<boolean>(useLocation().state?.updatePaymentMethod);
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const [invoice, setInvoice] = useState<Invoice>();
    const [invoiceLoading, setInvoiceLoading] = useState<boolean>(true);
    const [latestPayment, setLatestPayment] = useState<Payment | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<'card' | 'direct-debit'>('card');
    const [directDebitEnabled, setDirectDebitEnabled] = useState<boolean>(false);

    React.useEffect(() => {
        (async () => {
            const config = await limeApiGet<LimeConfig>('configuration', auth);
            setStripe(await loadStripe(config.stripeApiKey));
            await refreshInvoice();
            await refreshLatestPayment();
        })();

        const fetchFeatureFlags = async () => {
            let featureFlags = await getFeatureFlag(auth);
            setDirectDebitEnabled(featureFlags.directDebitEnabled);
        };

        fetchFeatureFlags();
    }, [auth]);

    async function refreshInvoice() {
        setInvoiceLoading(true);
        let orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        const data = await limeApiGet<Invoice[]>(`organisations/${orgUuid}/invoices`, auth);
        if (data.length > 0) {
            setInvoice(data[0]);
        }

        setInvoiceLoading(false);
    }

    async function refreshLatestPayment() {
        const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        const payments = await limeApiGet<Payment[]>(`organisations/${orgUuid}/payments`, auth);
        setLatestPayment(payments && payments.length > 0 ? payments[0] : null);
    }

    function displayAmount() {
        return latestPayment?.amount.toLocaleString('en-GB', {style: 'currency', currency: 'GBP'});
    }

    return (
        <div>
            {latestPayment && latestPayment.statusId === PaymentStatusId.Failed && (
                <div className="bg-action-required rounded-lg p-md mb-lg">
                    <div className="flex justify-start">
                        <img className="w-10 h-10 my-auto" src={AlertCircle} alt="alert"/>
                        <div className="mx-lg">
                            <h4>Automatic Payment Notice</h4>
                            <p>
                                You have an outstanding balance of {displayAmount()} due to issues with recent payment
                                attempt(s). If you change your payment method below, we'll automatically attempt to
                                charge the outstanding amount on your new card.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <h1 className={`mb-sm ${portalPageTitle}`}>Billing</h1>
            <h3 className="font-sans-co font-bold text-portal-teal text-h3 mb-sm">{updatePaymentMethod ? 'Change ' : 'Add '}payment method</h3>
            <p className={`${portalPageSubtitle} mb-lg`}>This card will be used to pay your monthly invoices for Limes products and
                services.</p>
            <div className="w-[30rem]">
                <LoadingSpinner loading={invoiceLoading}>
                    {invoice && stripe && (
                        <>
                            <div className={`w-full py-lg px-xl mb-md text-portal-teal ${portalCardSurface}`}>
                                <h3 className="font-sans-co font-bold text-h4">Payment details</h3>
                                <p className="text-p1">Your monthly payment amount is:</p>
                                <h4 className="text-h2 font-sans-co">
                                    {invoice ? invoice.total.toLocaleString('en-GB', {
                                        style: 'currency',
                                        currency: 'GBP'
                                    }) : '£-'}
                                </h4>
                            </div>
                            <div className={`w-full text-portal-teal ${portalCardSurface}`}>
                                <div className="px-xl pt-lg">
                                    <h3 className="mb-xs font-sans-co font-bold text-h4">Select a payment method</h3>
                                </div>
                                {directDebitEnabled && (
                                    <div className="py-md flex justify-center gap-sm flex-wrap">
                                        <button
                                            type="button"
                                            className={selectedMethod === 'card' ? `${portalSecondaryPillButton} bg-portal-nav-active` : portalSecondaryPillButton}
                                            onClick={() => setSelectedMethod('card')}
                                        >
                                            Card
                                        </button>

                                        <button
                                            type="button"
                                            className={selectedMethod === 'direct-debit' ? `${portalSecondaryPillButton} bg-portal-nav-active` : portalSecondaryPillButton}
                                            onClick={() => setSelectedMethod('direct-debit')}
                                        >
                                            Direct debit
                                        </button>
                                    </div>
                                )}
                                <Elements stripe={stripe}>
                                    {selectedMethod === 'card' ? (
                                        <PaymentCard stripe={stripe} payment={latestPayment}
                                                     onPaymentProcessed={refreshLatestPayment}/>
                                    ) : (
                                        directDebitEnabled && (
                                            <PaymentDirectDebit payment={latestPayment}
                                                                onPaymentProcessed={refreshLatestPayment}/>
                                        )
                                    )}
                                </Elements>
                            </div>
                        </>
                    )}
                </LoadingSpinner>
            </div>
        </div>
    );
}

export default PaymentMethod;